// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to SignupApiError.
// Activated by setting VITE_USE_MOCK_API=false; see signup.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  SignupApiError,
  type Invitation,
  type SendCodeInput,
  type SignupApi,
  type SignupApiErrorCode,
  type SignupInput,
  type SignupTerm,
  type VerifyCodeInput,
} from '../types/signup.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` ⇒ no usable response reached the FE. Two sub-cases (PRD 19 §8.2):
  //  • Client is offline ⇒ NETWORK, so callers show the connection message
  //    ("서버 응답 없음" row). navigator.onLine === false is a definitive offline signal.
  //  • Online but the request still failed with no usable body (timeout, connection
  //    reset, or a CORS-blocked / bodyless 5xx) ⇒ a server-side failure, NOT a client
  //    connection problem. Surface as UNKNOWN so callers show the action-specific
  //    message (인증번호 발송 실패 / 회원가입 처리 실패), never the connection message.
  if (!candidate.response) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (offline) {
      throw new SignupApiError('NETWORK', '네트워크 연결을 확인해주세요.');
    }
    throw new SignupApiError('UNKNOWN', '알 수 없는 오류가 발생했습니다.');
  }
  const payload = candidate.response?.data?.error;
  throw new SignupApiError(
    (payload?.code as SignupApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const signupHttpApi: SignupApi = {
  async verifyInvitation(token: string): Promise<Invitation> {
    try {
      const res = await httpClient.get<ApiEnvelope<Invitation>>(endpoints.auth.signupVerify, {
        params: { token },
      });
      const data = res.data?.data;
      if (!data || typeof data !== 'object') {
        throw new SignupApiError('UNKNOWN', '초대 정보를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  // US-SU-05 — token-gated, no Authorization header. Published Terms for the consent section.
  async listSignupTerms(token: string): Promise<SignupTerm[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<SignupTerm[]>>(endpoints.auth.signupTerms, {
        params: { token },
      });
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new SignupApiError('UNKNOWN', '약관 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  // 204 No Content. BE phone regex is ^010\d{8}$ — strip hyphens before sending.
  // invitationToken lets the BE refuse a phone that differs from the invite-prefilled one (SIGNUP_PHONE_MISMATCH).
  async sendCode(input: SendCodeInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.signupSendCode, {
        phone: input.phone.replace(/\D/g, ''),
        invitationToken: input.invitationToken,
      });
    } catch (e) {
      mapError(e);
    }
  },

  // 204 No Content. On success the BE marks the phone verified server-side (keyed by phone).
  async verifyCode(input: VerifyCodeInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.signupVerifyCode, {
        phone: input.phone.replace(/\D/g, ''),
        code: input.code,
      });
    } catch (e) {
      mapError(e);
    }
  },

  // 204 No Content. phone digits-only; confirmPassword is required and checked by the BE.
  async completeSignup(input: SignupInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.signup, {
        ...input,
        phone: input.phone.replace(/\D/g, ''),
      });
    } catch (e) {
      mapError(e);
    }
  },
};
